import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  ReferenceArea,
  LabelList,
} from "recharts";
import "./commonLineTable.css";



const ZONE_COLORS = {
  GREEN: { fill: "#F0F7F0", stroke: "#9AE98E" },
  MID: { fill: "#F9F6F3", stroke: "#FFB98A" },
  ALERT: { fill: "#FFF4F4", stroke: "#ED86BD" },
};

// Default line config (used by Site Adoption)
const DEFAULT_LINE_CONFIG = {
  acceptance: { stroke: "#008090", dotFill: "#23E7FF", dashed: true },
  adoption: { stroke: "#F3AED3", dotFill: "#ED86BD", dashed: false },
};

const CHART_KEY_SUFFIX = "___IDX___";

const parseWeekYear = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  const match = text.match(/wk\s*(\d+)\s*[_-]\s*(\d{4})/i);
  if (!match) return null;

  const week = Number(match[1]);
  const year = Number(match[2]);

  if (!Number.isFinite(week) || !Number.isFinite(year)) return null;
  return { week, year };
};

const formatXAxisWeekLabel = (value) => {
  const raw = String(value ?? "").replace(new RegExp(`${CHART_KEY_SUFFIX}\\d+$`), "");
  return raw.replace(/\s*[_-]\s*\d{4}\s*$/i, "").trim();
};

const CustomZoneTooltip = ({ active, payload, acc, adp, stackedBars }) => {
  if (active && payload?.length) {
    return (
      <div className="custom-tooltip">
        {payload.map((entry) => {
          const name = entry.name;
          const isLine = ["acceptance", "adoption"].includes(name.toLowerCase());
          
          const originalKey = stackedBars?.find(b => b.label === name || b.dataKey === name)?.originalKey || name;
          let displayValue = entry.value;
          if (entry.payload?.[originalKey] !== undefined) {
            displayValue = entry.payload[originalKey];
          }
          
          let formattedValue = displayValue;
          if (typeof displayValue === "number" && !isLine) {
            formattedValue = displayValue.toLocaleString("en-IN");
          } else if (isLine) {
            formattedValue = `${displayValue}%`;
          }

          let icon;
          if (name.toLowerCase() === "acceptance" && acc) {
            icon = (
              <svg width="24" height="12" style={{ flexShrink: 0, marginRight: 8 }}>
                <line x1="0" y1="6" x2="24" y2="6" stroke={acc.stroke} strokeWidth="2" strokeDasharray={acc.dashed ? "4 4" : undefined} />
                <circle cx="12" cy="6" r="4" fill={acc.dotFill} stroke={acc.stroke} strokeWidth="1" />
              </svg>
            );
          } else if (name.toLowerCase() === "adoption" && adp) {
            icon = (
              <svg width="24" height="12" style={{ flexShrink: 0, marginRight: 8 }}>
                <line x1="0" y1="6" x2="24" y2="6" stroke={adp.stroke} strokeWidth="2" strokeDasharray={adp.dashed ? "4 4" : undefined} />
                <circle cx="12" cy="6" r="4" fill={adp.dotFill} stroke={adp.stroke} strokeWidth="1" />
              </svg>
            );
          } else {
            icon = <span className="tooltip-dot" style={{ backgroundColor: entry.color, marginRight: 8, marginTop: 0 }} />;
          }

          return (
            <div key={name} className="tooltip-row" style={{ alignItems: "center" }}>
              <div className="tooltip-left" style={{ alignItems: "center" }}>
                {icon}
                <span className="tooltip-label">{name}</span>
              </div>
              <span className="tooltip-value">{formattedValue}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

/** Top and inside labels for one segment of a stacked bar. */
const StackedBarLabel = ({ x, y, width, height, index, bar, chartData }) => {
  const payload = chartData[index];
  const elements = [];

  if (typeof bar.getTopLabel === "function") {
    const topVal = bar.getTopLabel(payload);
    if (topVal !== null && topVal !== undefined && topVal !== "") {
      elements.push(
        <text
          key="top"
          x={x + width / 2}
          y={y - 8}
          fill="#667F93"
          fontSize={11}
          fontWeight={600}
          textAnchor="middle"
        >
          {topVal}
        </text>,
      );
    }
  }

  if (typeof bar.getInsideLabel === "function") {
    const insideVal = bar.getInsideLabel(payload);
    if (
      insideVal !== null &&
      insideVal !== undefined &&
      insideVal !== "" &&
      height > 10
    ) {
      elements.push(
        <text
          key="inside"
          x={x + width / 2}
          y={y + height / 2 + 4}
          fill="#fff"
          fontSize={11}
          fontWeight={600}
          textAnchor="middle"
        >
          {insideVal}
        </text>,
      );
    }
  }

  return <g>{elements}</g>;
};

/** Single-bar variant: shows the bar as a percentage of the tallest bar. */
const SingleBarPercentLabel = ({ x, y, width, value, maxBar }) => {
  if (!value) return null;
  const percent = Math.round((value / maxBar) * 100);
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fill="#2f3e46"
      fontSize="12"
      fontWeight="600"
    >
      {percent}%
    </text>
  );
};

/** Percentage label above (dy negative) or below (dy positive) a line point. */
const LinePercentLabel = ({ x, y, value, cfg, dy }) => (
  <text
    x={x}
    y={y + dy}
    textAnchor="middle"
    fill={cfg.labelColor || cfg.stroke}
    fontSize={12}
    fontWeight={600}
  >
    {value}%
  </text>
);

const CommonZoneLineBarChart = ({
  data,
  xKey = "week",
  stackedBars,
  lineConfig,
  yAxisDomain,
  yAxisTicks,
  yAxisTickFormatter,
  hideLeftAxis = true,
  lineAxisDomain,
}) => {
  const acc = { ...DEFAULT_LINE_CONFIG.acceptance, ...lineConfig?.acceptance };
  const adp = { ...DEFAULT_LINE_CONFIG.adoption, ...lineConfig?.adoption };
  const [activeZone, setActiveZone] = useState(null);

  const filteredData = useMemo(() => {
    if (!activeZone) return data;
    return data.filter((item) => item.zone === activeZone);
  }, [data, activeZone]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aParsed = parseWeekYear(a?.[xKey]);
      const bParsed = parseWeekYear(b?.[xKey]);

      if (aParsed && bParsed) {
        if (aParsed.year !== bParsed.year) return aParsed.year - bParsed.year;
        if (aParsed.week !== bParsed.week) return aParsed.week - bParsed.week;
      }

      return String(a?.[xKey] ?? "").localeCompare(String(b?.[xKey] ?? ""), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [filteredData, xKey]);

  const chartData = useMemo(
    () => sortedData.map((d, i) => ({ ...d, _chartKey: `${d[xKey]}${CHART_KEY_SUFFIX}${i}` })),
    [sortedData, xKey],
  );

  const BAR_LIMIT = 22;
  const BAR_WIDTH = 60;

  const chartWidth =
    chartData.length > BAR_LIMIT ? chartData.length * BAR_WIDTH : "100%";

  const maxBar = useMemo(() => {
    if (!chartData.length) return 0;
    if (stackedBars) {
      return Math.max(
        ...chartData.map((d) =>
          stackedBars.reduce((sum, b) => sum + (d[b.dataKey] || 0), 0),
        ),
      );
    }
    return Math.max(...chartData.map((d) => d.bar || 0));
  }, [chartData, stackedBars]);

  // Compute max percentage so we can size the right axis dynamically
  const maxPct = useMemo(() => {
    if (!chartData.length) return 10;
    const maxVal = Math.max(
      ...chartData.map((d) => Math.max(d.acceptance || 0, d.adoption || 0)),
    );
    return Math.max(maxVal, 1); // at least 1 to avoid 0 domain
  }, [chartData]);

  return (
    <div className="chart-wrapper">
      <div className="scroll-container">
        <div className="chart-inner">
          <ResponsiveContainer width={chartWidth} height={400}>
            <ComposedChart
              data={chartData}
              margin={{ top: 36, right: 20, bottom: 10, left: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />

              {/* Use unique _chartKey as XAxis dataKey; tickFormatter restores the display label */}
              <XAxis
                dataKey="_chartKey"
                tickFormatter={formatXAxisWeekLabel}
                tick={{ fill: "#667F93", fontSize: 12 }}
              />

              <YAxis
                yAxisId="left"
                domain={yAxisDomain || [0, Math.ceil(maxBar * 3)]}
                ticks={yAxisTicks}
                tickFormatter={yAxisTickFormatter}
                hide={hideLeftAxis}
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#667F93", fontSize: 12, fontFamily: "'UnileverShilling', sans-serif" }}
                width={50}
              />

              <YAxis
                yAxisId="right"
                orientation="right"
                domain={lineAxisDomain || [-(maxPct * 2.5), Math.ceil(maxPct * 1.4)]}
                hide
              />

              <Tooltip content={<CustomZoneTooltip acc={acc} adp={adp} stackedBars={stackedBars} />} />

              {/* Background zones — each entry has a unique _chartKey so the area renders correctly */}
              {chartData.map((entry) => {
                const zc = ZONE_COLORS[entry.zone] || ZONE_COLORS.ALERT;
                return (
                  <ReferenceArea
                    key={entry._chartKey}
                    x1={entry._chartKey}
                    x2={entry._chartKey}
                    yAxisId="left"
                    fill={zc.fill}
                    stroke={zc.stroke}
                    strokeOpacity={0.5}
                    fillOpacity={1}
                    ifOverflow="visible"
                  />
                );
              })}

              {stackedBars ? (
                stackedBars.map((bar, idx) => (
                  <Bar
                    key={bar.dataKey}
                    yAxisId="left"
                    name={bar.label || bar.dataKey}
                    dataKey={bar.dataKey}
                    fill={bar.color}
                    stackId={bar.stackId || "site"}
                    barSize={20}
                    radius={bar.radius || (idx === stackedBars.length - 1 ? [8, 8, 0, 0] : [0, 0, 0, 0])}
                  >
                    <LabelList
                      dataKey={bar.dataKey}
                      content={<StackedBarLabel bar={bar} chartData={chartData} />}
                    />
                  </Bar>
                ))
              ) : (
                <Bar
                  yAxisId="left"
                  dataKey="bar"
                  fill="#0F5C63"
                  barSize={30}
                  radius={[8, 8, 0, 0]}
                >
                  <LabelList
                    content={<SingleBarPercentLabel maxBar={maxBar} />}
                  />
                  <LabelList
                    dataKey="bar"
                    position="insideTop"
                    style={{ fill: "#fff", fontWeight: 600, fontSize: 12 }}
                  />
                </Bar>
              )}

              {/* Acceptance Line — labels pushed above dot */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="acceptance"
                stroke={acc.stroke}
                strokeDasharray={acc.dashed ? "5 4" : undefined}
                strokeWidth={2}
                dot={{
                  r: 4,
                  fill: acc.dotFill,
                  stroke: acc.stroke,
                  strokeWidth: 1.5,
                }}
                activeDot={{ r: 6 }}
              >
                <LabelList
                  dataKey="acceptance"
                  content={<LinePercentLabel cfg={acc} dy={-14} />}
                />
              </Line>

              {/* Adoption Line — labels pushed below dot */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="adoption"
                stroke={adp.stroke}
                strokeDasharray={adp.dashed ? "5 4" : undefined}
                strokeWidth={2}
                dot={{
                  r: 4,
                  fill: adp.dotFill,
                  stroke: adp.stroke,
                  strokeWidth: 1.5,
                }}
                activeDot={{ r: 6 }}
              >
                <LabelList
                  dataKey="adoption"
                  content={<LinePercentLabel cfg={adp} dy={20} />}
                />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Custom Legend */}
      <div className="zone-legend">
        {/* Acceptance */}
        <div className="legend-item">
          <svg width="32" height="12" style={{ flexShrink: 0 }}>
            <line
              x1="0"
              y1="6"
              x2="32"
              y2="6"
              stroke={acc.stroke}
              strokeWidth="2"
              strokeDasharray={acc.dashed ? "5 3" : undefined}
            />
            <circle
              cx="16"
              cy="6"
              r="4"
              fill={acc.dotFill}
              stroke={acc.stroke}
              strokeWidth="1.5"
            />
          </svg>
          Acceptance
        </div>

        {/* Adoption */}
        <div className="legend-item">
          <svg width="32" height="12" style={{ flexShrink: 0 }}>
            <line
              x1="0"
              y1="6"
              x2="32"
              y2="6"
              stroke={adp.stroke}
              strokeWidth="2"
              strokeDasharray={adp.dashed ? "5 3" : undefined}
            />
            <circle
              cx="16"
              cy="6"
              r="4"
              fill={adp.dotFill}
              stroke={adp.stroke}
              strokeWidth="1.5"
            />
          </svg>
          Adoption
        </div>

        {/* Dynamic bar legend items (Open / Accepted / Rejected) */}
        {stackedBars
          ? stackedBars.map((bar) => (
              <div key={bar.dataKey} className="legend-item">
                <span
                  className="legend-dot"
                  style={{ backgroundColor: bar.color }}
                />{" "}
                {bar.label}
              </div>
            ))
          : (
              <div className="legend-item">
                <span
                  className="legend-dot"
                  style={{ backgroundColor: "#0F5C63" }}
                />{" "}
                Acceptance
              </div>
            )}

        {/* Zone filter items — clickable to filter chart */}
        <button
          type="button"
          className="legend-item legend-item-button"
          aria-pressed={activeZone === "GREEN"}
          onClick={() => setActiveZone(activeZone === "GREEN" ? null : "GREEN")}
        >
          <span
            className={`box green ${activeZone === "GREEN" ? "active" : ""}`}
          />{" "}
          Green zone
        </button>
        <button
          type="button"
          className="legend-item legend-item-button"
          aria-pressed={activeZone === "MID"}
          onClick={() => setActiveZone(activeZone === "MID" ? null : "MID")}
        >
          <span
            className={`box mid ${activeZone === "MID" ? "active" : ""}`}
          />{" "}
          Mid zone
        </button>
        <button
          type="button"
          className="legend-item legend-item-button"
          aria-pressed={activeZone === "ALERT"}
          onClick={() => setActiveZone(activeZone === "ALERT" ? null : "ALERT")}
        >
          <span
            className={`box alert ${activeZone === "ALERT" ? "active" : ""}`}
          />{" "}
          Alert zone
        </button>
      </div>
    </div>
  );
};

const lineConfigShape = PropTypes.shape({
  stroke: PropTypes.string,
  dotFill: PropTypes.string,
  dashed: PropTypes.bool,
  labelColor: PropTypes.string,
});

const stackedBarShape = PropTypes.shape({
  dataKey: PropTypes.string.isRequired,
  originalKey: PropTypes.string,
  color: PropTypes.string,
  label: PropTypes.string,
  stackId: PropTypes.string,
  radius: PropTypes.arrayOf(PropTypes.number),
  getTopLabel: PropTypes.func,
  getInsideLabel: PropTypes.func,
});

CustomZoneTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      color: PropTypes.string,
      payload: PropTypes.object,
    }),
  ),
  acc: lineConfigShape,
  adp: lineConfigShape,
  stackedBars: PropTypes.arrayOf(stackedBarShape),
};

StackedBarLabel.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  index: PropTypes.number,
  bar: stackedBarShape.isRequired,
  chartData: PropTypes.arrayOf(PropTypes.object).isRequired,
};

SingleBarPercentLabel.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxBar: PropTypes.number,
};

LinePercentLabel.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  cfg: lineConfigShape.isRequired,
  dy: PropTypes.number.isRequired,
};

CommonZoneLineBarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  xKey: PropTypes.string,
  stackedBars: PropTypes.arrayOf(stackedBarShape),
  lineConfig: PropTypes.shape({
    acceptance: lineConfigShape,
    adoption: lineConfigShape,
  }),
  yAxisDomain: PropTypes.arrayOf(PropTypes.number),
  yAxisTicks: PropTypes.arrayOf(PropTypes.number),
  yAxisTickFormatter: PropTypes.func,
  hideLeftAxis: PropTypes.bool,
  lineAxisDomain: PropTypes.arrayOf(PropTypes.number),
};

export default CommonZoneLineBarChart;
